/* eslint-disable @typescript-eslint/naming-convention */
import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { CameraSource, CameraResultType, Camera, Photo } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Preferences } from '@capacitor/preferences';
import { Platform, AlertController, ActionSheetController, LoadingController } from '@ionic/angular';

interface Imagem {
  filepath: string;
  webviewPath: string;
  base64?: string;
  tipo?: string;
}

@Injectable({
  providedIn: 'root',
})
export class UIService {
  public APP_VERSION = '6.1.1';
  public imagens: Imagem[] = [];
  private IMAGE_STORAGE = 'imagens';
  private APP_MODO = 'appmodo';

  constructor(
    private platform: Platform,
    private datepipe: DatePipe,
    public alertController: AlertController,
    private actionSheetController: ActionSheetController,
    public loadingController: LoadingController,
  ) { }

  async setAppModo(modo: string) {
    return Preferences.set({ key: this.APP_MODO, value: modo });
  }

  getAppModo() {
    return Preferences.get({ key: this.APP_MODO });
  }

  async alerta(msg: string, titulo?: string) {
    const alert = await this.alertController.create({
      header: 'Atenção!!',
      subHeader: titulo,
      message: msg,
      buttons: ['OK'],
    });

    alert.present();
    return await alert.onDidDismiss();
  }

  async confirma(
    msg: string,
    titulo?: string,
    inputinfo?: string,
    password?: boolean
  ): Promise<any> {
    const opcoes = {
      header: 'Confirmar?',
      subHeader: titulo,
      message: msg,
      inputs: [],
      buttons: [
        {
          text: 'Não',
          handler: () => {
            alert.dismiss(false);
            return false;
          },
        },
        {
          text: 'Sim',
          handler: (data) => {
            if (data) {
              if (typeof data.info != null) {
                alert.dismiss(data);
              } else {
                alert.dismiss(true);
              }
            } else {
              alert.dismiss(true);
            }
            return false;
          },
        },
      ],
    };

    if (inputinfo) {
      let tipo = 'text';
      if (password) {
        tipo = 'password';
      }
      opcoes.inputs = [
        {
          name: 'info',
          type: tipo,
          placeholder: inputinfo,
        },
      ];
    }

    const alert = await this.alertController.create(opcoes);

    alert.present();
    return await alert.onDidDismiss();
  }


  // Show the loader for infinite time
  showLoader() {
    this.loadingController
      .create({
        message: 'Um momento por favor...',
        //duration: 3000,
      })
      .then((res) => {
        res.present();
      });
  }

  // Hide the loader if already created otherwise return error
  hideLoader() {
    this.loadingController
      .dismiss()
      .then((res) => {
        // console.log('Loading dismissed!', res);
      })
      .catch((error) => {
        console.log('error', error);
      });
  }

  async showHideAutoLoader() {
    const loading = await this.loadingController.create({
      message: 'This Loader Will Auto Hide in 2 Seconds',
      duration: 2000,
    });
    await loading.present();

    const { role, data } = await loading.onDidDismiss();
    console.log('Loading dismissed! after 2 Seconds', { role, data });
  }


  getISODate(data) {
    if (!data) { return null; }
    const [day, month, year] = data.split('/');
    const date = new Date(+year, +month - 1, +day);
    return this.datepipe.transform(date, 'yyyy-MM-dd');
  }

  showRemoteFile(url, contenttype = 'application/pdf') {

    window.open(encodeURI(url), '_blank', 'location=yes');
  }

  limparImagens() {
    this.imagens = [];
  }

  async adicionarImagem(salvar: boolean = false) {
    const actionSheet = await this.actionSheetController.create({
      header: 'Origem da Imagem ?',
      buttons: [
        {
          text: 'Buscar na Galeria',
          handler: () => {
            this.capturarImagem(CameraSource.Prompt, salvar);
          },
        },
        {
          text: 'Tirar uma Foto',
          handler: () => {
            this.capturarImagem(CameraSource.Camera, salvar);
          },
        },
        {
          text: 'Cancel',
          role: 'cancel',
        },
      ],
    });
    await actionSheet.present();
  }

  public async apagarImagem(photo, position) {
    const actionSheet = await this.actionSheetController.create({
      header: 'Documentos',
      buttons: [
        {
          text: 'Apagar',
          role: 'destructive',
          icon: 'trash',
          handler: () => {
            this.removerImagem(photo, position);
          },
        },
        {
          text: 'Ignorar',
          icon: 'close',
          role: 'cancel',
          handler: () => {
            // Nothing to do, action sheet is automatically closed
          },
        },
      ],
    });
    await actionSheet.present();
  }

  public async capturarImagem(
    sourceType: CameraSource,
    salvar: boolean = false
  ) {
    let resultType = CameraResultType.Uri;
    if (!salvar) {
      resultType = CameraResultType.Base64;
    }

    const options = {
      resultType,
      source: sourceType,
      quality: 100,
    };

    const capturedImage = await Camera.getPhoto(options);

    if (!salvar) {
      this.imagens.unshift({
        filepath: null,
        webviewPath: null,
        base64: 'data:image/jpeg;base64,' + capturedImage.base64String,
      });
    } else {
      const savedImageFile = await this.salvarImagem(capturedImage);
      this.imagens.unshift(savedImageFile);

      Preferences.set({
        key: this.IMAGE_STORAGE,
        value: this.platform.is('hybrid')
          ? JSON.stringify(this.imagens)
          : JSON.stringify(
            this.imagens.map((p) => {
              // Don't save the base64 representation of the photo data,
              // since it's already saved on the Filesystem
              const photoCopy = { ...p };
              delete photoCopy.base64;
              return photoCopy;
            })
          ),
      });
    }
  }

  public async carregarImagens() {
    // Retrieve cached photo array data
    const img = await Preferences.get({ key: this.IMAGE_STORAGE });
    this.imagens = JSON.parse(img.value) || [];

    // Easiest way to detect when running on the web:
    // “when the platform is NOT hybrid, do this”
    if (!this.platform.is('hybrid')) {
      // Display the photo by reading into base64 format
      for (const photo of this.imagens) {
        // Read each saved photo's data from the Filesystem
        const readFile = await Filesystem.readFile({
          path: photo.filepath,
          directory: Directory.Data,
        });

        // Web platform only: Save the photo into the base64 field
        photo.base64 = `data:image/jpeg;base64,${readFile.data}`;
      }
    }
  }


  public convertBlobToBase64 = (blob: Blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = () => {
        resolve(reader.result);
      };
      reader.readAsDataURL(blob);
    });

  private async salvarImagem(cameraPhoto: Photo) {
    // Convert photo to base64 format, required by Filesystem API to save
    const base64Data = await this.readAsBase64(cameraPhoto);

    // Write the file to the data directory
    const fileName = new Date().getTime() + '.jpeg';
    const savedFile = await Filesystem.writeFile({
      path: fileName,
      data: base64Data,
      directory: Directory.Data,
    });

    if (this.platform.is('hybrid')) {
      // Display the new image by rewriting the 'file://' path to HTTP
      // Details: https://ionicframework.com/docs/building/webview#file-protocol
      return {
        filepath: savedFile.uri,
        webviewPath: Capacitor.convertFileSrc(savedFile.uri),
      };
    } else {
      // Use webPath to display the new image instead of base64 since it's
      // already loaded into memory
      return {
        filepath: fileName,
        webviewPath: cameraPhoto.webPath,
      };
    }
  }

  private async readAsBase64(cameraPhoto: Photo) {
    // "hybrid" will detect Cordova or Capacitor
    if (this.platform.is('hybrid')) {
      // Read the file into base64 format
      const file = await Filesystem.readFile({
        path: cameraPhoto.path,
      });
      return file.data;
    } else {
      // Fetch the photo, read as a blob, then convert to base64 format
      const response = await fetch(cameraPhoto.webPath);
      const blob = await response.blob();

      return (await this.convertBlobToBase64(blob)) as string;
    }
  }

  private async removerImagem(photo: Imagem, position: number) {
    // Remove this photo from the Photos reference data array
    this.imagens.splice(position, 1);
    // Update photos array cache by overwriting the existing photo array

    Preferences.set({
      key: this.IMAGE_STORAGE,
      value: JSON.stringify(this.imagens),
    });

    if (photo.filepath != null) {
      // delete photo file from filesystem
      const filename = photo.filepath.substr(
        photo.filepath.lastIndexOf('/') + 1
      );

      await Filesystem.deleteFile({
        path: filename,
        directory: Directory.Data,
      });
    }
  }


}
